import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { analyticsAllowed, trackAnalyticsEvent, trackPageView } from "../analytics/siteAnalytics";

function AnalyticsTracker() {
  const location = useLocation();
  const pageKey = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (!analyticsAllowed(location.pathname)) return;

    trackPageView(location.pathname, location.search);

    const reachedDepths = new Set();
    let activeSeconds = 0;
    let lastInteractionAt = Date.now();

    const markActive = () => {
      lastInteractionAt = Date.now();
    };

    const checkScrollDepth = () => {
      markActive();

      const scrollHeight = document.documentElement.scrollHeight;
      const viewedBottom = window.scrollY + window.innerHeight;
      const percent = scrollHeight > 0 ? Math.min(100, Math.round((viewedBottom / scrollHeight) * 100)) : 100;

      [25, 50, 75, 100].forEach((depth) => {
        if (percent >= depth && !reachedDepths.has(depth)) {
          reachedDepths.add(depth);

          trackAnalyticsEvent("scroll_depth", {
            pagePath: pageKey,
            depth,
          });
        }
      });
    };

    const activeTimer = setInterval(() => {
      const active = document.visibilityState === "visible" && Date.now() - lastInteractionAt <= 60000;

      if (active) activeSeconds += 1;
    }, 1000);

    const flushActiveTime = () => {
      if (activeSeconds < 1) return;

      const seconds = activeSeconds;
      activeSeconds = 0;

      trackAnalyticsEvent("active_time", {
        pagePath: pageKey,
        seconds,
      });
    };

    const flushTimer = setInterval(flushActiveTime, 120000);

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") flushActiveTime();
      else markActive();
    };

    window.addEventListener("scroll", checkScrollDepth, { passive: true });
    window.addEventListener("pointerdown", markActive, { passive: true });
    window.addEventListener("touchstart", markActive, { passive: true });
    window.addEventListener("keydown", markActive);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", flushActiveTime);

    checkScrollDepth();

    return () => {
      flushActiveTime();

      clearInterval(activeTimer);
      clearInterval(flushTimer);

      window.removeEventListener("scroll", checkScrollDepth);
      window.removeEventListener("pointerdown", markActive);
      window.removeEventListener("touchstart", markActive);
      window.removeEventListener("keydown", markActive);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", flushActiveTime);
    };
  }, [pageKey, location.pathname, location.search]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const target = event.target.closest("a, button, [data-analytics]");
      if (!target) return;

      const href = target.getAttribute("href") || "";
      const analyticsId = target.getAttribute("data-analytics") || "";
      const analyticsLabel =
        target.getAttribute("data-analytics-label") ||
        target.getAttribute("aria-label") ||
        target.textContent?.trim().replace(/\s+/g, " ").slice(0, 160) ||
        analyticsId ||
        "";

      let type = target.tagName === "BUTTON" || analyticsId ? "button_click" : "link_click";

      if (href.startsWith("mailto:")) type = "email_click";
      else if (href.startsWith("tel:")) type = "phone_click";
      else if (/^https?:\/\//i.test(href)) {
        try {
          if (new URL(href).origin !== window.location.origin) type = "external_link_click";
        } catch {
          type = "link_click";
        }
      }

      trackAnalyticsEvent(type, {
        analyticsId,
        analyticsLabel,
        href,
        elementType: target.tagName.toLowerCase(),
      });
    };

    const handleCopy = () => {
      const copiedText = window.getSelection()?.toString() || "";
      if (!copiedText.trim()) return;

      trackAnalyticsEvent("copy_to_clipboard", {
        copiedText: copiedText.trim().slice(0, 5000),
        copiedCharacterCount: copiedText.length,
      });
    };

    document.addEventListener("click", handleClick);
    document.addEventListener("copy", handleCopy);

    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("copy", handleCopy);
    };
  }, []);

  useEffect(() => {
    if (!analyticsAllowed(location.pathname)) return;

    let consecutiveDetections = 0;
    let reportedOpen = false;

    const DEVTOOLS_GAP_THRESHOLD = 160;

    const checkDeveloperTools = () => {
      const widthGap = Math.max(0, window.outerWidth - window.innerWidth);

      const heightGap = Math.max(0, window.outerHeight - window.innerHeight);

      const likelyOpen = widthGap > DEVTOOLS_GAP_THRESHOLD || heightGap > DEVTOOLS_GAP_THRESHOLD;

      if (likelyOpen) {
        consecutiveDetections += 1;
      } else {
        consecutiveDetections = 0;
        reportedOpen = false;
      }

      /*
        Require several consecutive detections so
        ordinary resizing is less likely to count.
      */
      if (consecutiveDetections >= 3 && !reportedOpen) {
        reportedOpen = true;

        trackAnalyticsEvent("developer_tools_detected", {
          pagePath: pageKey,
          detectionMethod: "window_gap",
          widthGap,
          heightGap,
        });
      }
    };

    const handleDeveloperToolsShortcut = (event) => {
      const key = String(event.key || "").toLowerCase();

      const commandKey = event.ctrlKey || event.metaKey;

      const devToolsShortcut = event.key === "F12" || (commandKey && event.shiftKey && ["i", "j", "c"].includes(key));

      if (!devToolsShortcut) return;

      const shortcut = event.key === "F12" ? "F12" : `${event.metaKey ? "Cmd" : "Ctrl"}+Shift+${key.toUpperCase()}`;

      trackAnalyticsEvent("developer_tools_shortcut", {
        pagePath: pageKey,
        shortcut,
      });
    };

    const detectorTimer = setInterval(checkDeveloperTools, 1500);

    window.addEventListener("keydown", handleDeveloperToolsShortcut);

    checkDeveloperTools();

    return () => {
      clearInterval(detectorTimer);

      window.removeEventListener("keydown", handleDeveloperToolsShortcut);
    };
  }, [pageKey, location.pathname]);

  return null;
}

export default AnalyticsTracker;
